/**
 * AIPresence - AI collaborator presence UI components
 *
 * Provides visual feedback for AI agent activity on the canvas:
 * - AICursor: Shows AI cursor position when working on canvas
 * - AIWorkingIndicator: Fixed indicator showing AI thinking/typing status
 * - AIPresence: Main component combining both based on awareness state
 *
 * Uses violet theme color (#8B5CF6) to match AI node styling from Phase 6.
 *
 * Phase 7: AI Ghost Writer foundation
 */

import { type RefObject } from 'react';
import { useReactFlow } from '@xyflow/react';
import { AI_CLIENT_ID, type AIAwarenessState } from '@deep-agents/shared-types';

// ============================================================================
// AI WORKING INDICATOR
// ============================================================================

interface AIWorkingIndicatorProps {
  /** True when AI is processing/generating */
  isThinking: boolean;
  /** True when AI is streaming text output */
  isTyping: boolean;
  /** Optional description of current task */
  taskDescription?: string;
}

/**
 * AIWorkingIndicator - Fixed position indicator showing AI status.
 *
 * Positioned in bottom-right corner of canvas.
 * Shows pulsing dot when thinking, ping animation when typing.
 */
export function AIWorkingIndicator({
  isThinking,
  isTyping,
  taskDescription,
}: AIWorkingIndicatorProps) {
  // Don't render if AI is idle
  if (!isThinking && !isTyping) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '1rem',
        right: '1rem',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.5rem 0.75rem',
        backgroundColor: '#EDE9FE', // violet-100
        border: '1px solid #C4B5FD', // violet-300
        borderRadius: '0.5rem',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      }}
    >
      {/* Status dot with animation */}
      <div
        style={{
          position: 'relative',
          width: '0.5rem',
          height: '0.5rem',
        }}
      >
        {/* Base dot */}
        <div
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: '#8B5CF6', // violet-500
            borderRadius: '50%',
          }}
        />
        {/* Pulse animation when thinking */}
        {isThinking && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: '#8B5CF6',
              borderRadius: '50%',
              animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
            }}
          />
        )}
        {/* Ping animation when typing */}
        {isTyping && (
          <div
            style={{
              position: 'absolute',
              top: '-50%',
              left: '-50%',
              width: '200%',
              height: '200%',
              backgroundColor: '#8B5CF6',
              borderRadius: '50%',
              opacity: 0.75,
              animation: 'ping 1s cubic-bezier(0, 0, 0.2, 1) infinite',
            }}
          />
        )}
      </div>

      {/* Status text */}
      <span
        style={{
          fontSize: '0.875rem',
          fontWeight: 500,
          color: '#6D28D9', // violet-700
        }}
      >
        {isTyping
          ? `AI is typing${taskDescription ? `: ${taskDescription}` : '...'}`
          : `AI is thinking${taskDescription ? `: ${taskDescription}` : '...'}`}
      </span>

      {/* CSS animations injected via style tag */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes ping {
          75%, 100% {
            transform: scale(2);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}

// ============================================================================
// AI CURSOR
// ============================================================================

interface AICursorProps {
  /** Canvas position { x, y } */
  position: { x: number; y: number };
  /** Container ref for coordinate calculations */
  containerRef: RefObject<HTMLDivElement | null>;
}

/**
 * AICursor - Canvas cursor for AI agent.
 *
 * Similar to RemoteCursors but with AI-specific styling.
 * Uses violet theme color and "AI" label.
 */
export function AICursor({ position, containerRef }: AICursorProps) {
  const { flowToScreenPosition } = useReactFlow();
  const containerRect = containerRef.current?.getBoundingClientRect();

  // Convert flow coordinates to screen coordinates for rendering
  const screenPos = flowToScreenPosition({ x: position.x, y: position.y });
  const left = containerRect ? screenPos.x - containerRect.left : screenPos.x;
  const top = containerRect ? screenPos.y - containerRect.top : screenPos.y;

  return (
    <div
      style={{
        position: 'absolute',
        left,
        top,
        zIndex: 9999,
        pointerEvents: 'none',
      }}
    >
      {/* AI Cursor arrow SVG - violet color */}
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: 'block' }}
      >
        <path
          d="M5 2L19 12L12 13L9 20L5 2Z"
          fill="#8B5CF6" // violet-500
          stroke="white"
          strokeWidth="2"
          strokeLinejoin="round"
        />
      </svg>

      {/* AI cursor label */}
      <div
        style={{
          position: 'absolute',
          left: '16px',
          top: '20px',
          backgroundColor: '#8B5CF6', // violet-500
          color: 'white',
          fontSize: '12px',
          fontWeight: 600,
          padding: '2px 8px',
          borderRadius: '4px',
          whiteSpace: 'nowrap',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        {/* AI sparkle icon */}
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 2L13.09 8.26L19 9.27L14.5 13.97L15.82 20L12 16.9L8.18 20L9.5 13.97L5 9.27L10.91 8.26L12 2Z"
            fill="currentColor"
          />
        </svg>
        AI
      </div>
    </div>
  );
}

// ============================================================================
// AI PRESENCE (Main Component)
// ============================================================================

interface AIPresenceProps {
  /** Awareness states from all connected clients */
  states: Map<number, unknown>;
  /** Container ref for coordinate calculations */
  containerRef: RefObject<HTMLDivElement | null>;
}

/**
 * AIPresence - Main component rendering AI cursor and working indicator.
 *
 * Checks for AI_CLIENT_ID in awareness states and renders:
 * - AICursor if canvasCursor is set
 * - AIWorkingIndicator if isThinking or isTyping
 */
export function AIPresence({ states, containerRef }: AIPresenceProps) {
  // Look for AI state in awareness
  const aiState = states.get(AI_CLIENT_ID) as AIAwarenessState | undefined;

  // Don't render anything if no AI state
  if (!aiState) {
    return null;
  }

  const { canvasCursor, isThinking, isTyping, taskDescription } = aiState;

  return (
    <>
      {/* AI cursor on canvas */}
      {canvasCursor && (
        <AICursor position={canvasCursor} containerRef={containerRef} />
      )}

      {/* AI working indicator */}
      <AIWorkingIndicator
        isThinking={isThinking}
        isTyping={isTyping}
        taskDescription={taskDescription}
      />
    </>
  );
}
